import React from 'react';
import Input from '../../Components/Input';
import { BuilderActionType, BuilderContext } from './state';
import { getStyleProperties } from '../../utils/builderUtils';
import NumberInput from './NumberInput';
import { Crosshair, Move } from '../../Components/Icon';
import { BuilderPanel, BuilderPillButton } from './BuilderPanel';

type Props = {
    selector: string;
};

function PositionSelector({ selector }: Props) {
    const styleObj = getStyleProperties(selector, [
        'top',
        'left',
        'bottom',
        'right',
        'transform',
    ]);
    const [position, setPosition] = React.useState({ ...styleObj });

    const { dispatch } = React.useContext(BuilderContext);

    const handleChange = (name: string, value: string) => {
        setPosition({ ...position, [name]: value });
        dispatch({
            type: BuilderActionType.UPDATE_CSS,
            payload: { selector, properties: { [name]: value } },
        });
    };

    const handleCenter = (type: 'VERTICAL' | 'HORIZONTAL' | 'BOTH') => {
        let temp = { ...position };
        switch (type) {
            case 'VERTICAL':
                temp = { ...temp, top: '50%', transform: 'translate(0, -50%)' };
                break;
            case 'HORIZONTAL':
                temp = {
                    ...temp,
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                };
                break;
            case 'BOTH':
                temp = {
                    ...temp,
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                };
                break;
            default:
                break;
        }
        setPosition(temp);
        dispatch({
            type: BuilderActionType.UPDATE_CSS,
            payload: { selector, properties: temp },
        });
    };

    return (
        <BuilderPanel
            title="Position"
            icon={<Move className="h-4 w-4" />}
            className="mt-5"
            bodyClassName="space-y-4"
        >
            <div className="flex flex-wrap gap-2">
                <BuilderPillButton
                    onClick={() => handleCenter('HORIZONTAL')}
                    icon={<Crosshair className="h-4 w-4" />}
                >
                    Center X
                </BuilderPillButton>
                <BuilderPillButton
                    onClick={() => handleCenter('VERTICAL')}
                    icon={<Crosshair className="h-4 w-4" />}
                >
                    Center Y
                </BuilderPillButton>
                <BuilderPillButton
                    onClick={() => handleCenter('BOTH')}
                    icon={<Crosshair className="h-4 w-4" />}
                >
                    Center XY
                </BuilderPillButton>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Top
                    </p>
                    <NumberInput
                        options={['%', 'px']}
                        defaultOption="%"
                        value={position.top}
                        onChange={handleChange}
                        name="top"
                    />
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Left
                    </p>
                    <NumberInput
                        options={['%', 'px']}
                        defaultOption="%"
                        value={position.left}
                        onChange={handleChange}
                        name="left"
                    />
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Bottom
                    </p>
                    <NumberInput
                        options={['%', 'px']}
                        defaultOption="%"
                        value={position.bottom}
                        onChange={handleChange}
                        name="bottom"
                    />
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Right
                    </p>
                    <NumberInput
                        options={['%', 'px']}
                        defaultOption="%"
                        value={position.right}
                        onChange={handleChange}
                        name="right"
                    />
                </div>
            </div>
            <div className="max-w-xs">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Transform
                </p>
                <Input
                    value={position.transform}
                    onChange={(e) => handleChange('transform', e.target.value)}
                />
            </div>
        </BuilderPanel>
    );
}

export default PositionSelector;
